import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Money, gbp } from "@/components/hub/Shell";
import { Loader2, Layers } from "lucide-react";

/**
 * DealValueCard — what this one case is worth to Connexis, worked out by the same
 * engine the pipeline uses (`/hub/leads/{id}/value`), so a figure can never disagree
 * with the one on the ledger. Re-reads whenever the case changes.
 */
export function DealValueCard({ leadId, watch }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try { const r = await api.get(`/hub/leads/${leadId}/value`); if (alive) setD(r.data); }
      catch { if (alive) setD(null); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [leadId, watch]);

  if (loading) {
    return (
      <div className="hub-card grid place-items-center rounded-[14px] border border-slate-200/70 bg-white py-10 text-slate-300">
        <Loader2 className="size-5 animate-spin" />
      </div>
    );
  }
  if (!d) return null;
  const v = d.value;

  return (
    <div className="hub-card relative overflow-hidden rounded-[14px] border border-slate-200/70 bg-white p-5
                    before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-[#0055FF] before:content-['']"
         data-testid="lead-deal-value">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
          <Layers className="size-3 text-[#0055FF]" /> What this deal pays us
        </div>
        <span className="hub-num text-[10.5px] text-slate-400">{v.confidence_pct}% likely at this stage</span>
      </div>

      <div className="mt-4 divide-y divide-slate-100">
        {(v.lines || []).map((l) => (
          <div key={l.key} className="flex items-start gap-4 py-2.5" data-testid={`lead-value-line-${l.key}`}>
            <div className="min-w-0 flex-1">
              <div className={`text-[12.5px] ${l.kind === "net" ? "font-semibold text-slate-900" : "text-slate-700"}`}>{l.label}</div>
              {l.detail && <div className="mt-0.5 text-[11px] leading-snug text-slate-400">{l.detail}</div>}
            </div>
            {l.kind === "pending"
              ? <span className="w-[120px] shrink-0 text-right text-[11px] text-slate-400">not yet payable</span>
              : <Money value={l.kind === "out" ? -l.amount_gbp : l.amount_gbp}
                       tone={l.kind === "in" ? "in" : l.kind === "out" ? "out" : "net"}
                       size={l.kind === "net" ? "md" : "sm"}
                       className={`w-[120px] shrink-0 text-right ${l.kind === "net" ? "font-semibold" : ""}`} />}
          </div>
        ))}
      </div>

      <p className="mt-3.5 border-t border-slate-100 pt-3 text-[11px] leading-relaxed text-slate-400">
        {v.basis}. {v.priced
          ? `Weighted at this stage that's ${gbp(v.weighted_net_gbp)}${v.margin_pct !== null && v.margin_pct !== undefined ? ` — we keep ${v.margin_pct}% of the lender fee` : ""}.`
          : "Route it to a desk and the fee, the deductions and the net all appear here."}
      </p>
    </div>
  );
}
